// ALGOLIA V5 - THE CORRECT WAY
import { algoliasearch } from "algoliasearch";
import {
  collection,
  doc,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";
import { filter } from "jszip";
import { db } from "../config/FirebaseConfig";

// Validate environment variables
const appId = process.env.REACT_APP_ALGOLIA_APP_ID;
const searchKey =
  process.env.REACT_APP_ALGOLIA_SEARCH_KEY ||
  process.env.REACT_APP_ALGOLIA_SEARCH;

if (!appId || !searchKey) {
  console.error("Missing Algolia environment variables:", {
    appId: !!appId,
    searchKey: !!searchKey,
  });
  throw new Error(
    "REACT_APP_ALGOLIA_APP_ID and REACT_APP_ALGOLIA_SEARCH_KEY must be set"
  );
}

const searchClient = algoliasearch(appId, searchKey);

/**
 * Query an Algolia index with filters + search term (V5 compatible)
 * @param {string} indexName - Name of the Algolia index (e.g., "folder")
 * @param {Array} filtersArr - Array of filter objects [{ field, operator, value }]
 * @param {string} searchTerm - The text to search for ("" means no term)
 * @param {number} limit - Max hits to return
 * @param {string} sortingField - Attribute to sort by (must be set as sortable in Algolia)
 * @param {string} sortingOrder - "asc" or "desc"
 */
export async function queryCollectionAlgolia(
  indexName,
  filtersArr = [],
  searchTerm = "",
  limit = 50,
  overrideAlgoliaFilters = null
) {
  console.log("queryCollectionAlgolia ");
  if (!indexName) {
    console.error("Index name is missing");
    return [];
  }

  // Convert filters array into Algolia filter string
  let filtersStr = "";
  if (filtersArr && filtersArr.length > 0) {
    filtersStr = filtersArr
      .map((f) => {
        const { field, operator, value } = f;

        if (operator === "==" || operator === "===") {
          // Values with spaces MUST be quoted
          if (typeof value === "string" && value.includes(" ")) {
            const escapedValue = value.replace(/"/g, '\\"');
            return `${field}:"${escapedValue}"`;
          } else {
            // Simple values without spaces don't need quotes
            return `${field}:${value}`;
          }
        } else if (["<", "<=", ">", ">="].includes(operator)) {
          return `${field}${operator}${value}`;
        } else if (operator === "!=") {
          const escapedValue =
            typeof value === "string" ? value.replace(/"/g, '\\"') : value;
          return `NOT ${field}:"${escapedValue}"`;
        } else if (operator === "in" && Array.isArray(value)) {
          const orFilters = value.map((v) => {
            const escapedValue =
              typeof v === "string" ? v.replace(/"/g, '\\"') : v;
            return `${field}:"${escapedValue}"`;
          });
          return `(${orFilters.join(" OR ")})`;
        }

        console.warn(`Unsupported operator: ${operator}`);
        return "";
      })
      .filter(Boolean)
      .join(" AND ");
  }
  if (overrideAlgoliaFilters) {
    filtersStr = overrideAlgoliaFilters;
  }
  // V5: Build search parameters object
  const searchParams = {
    query: searchTerm || "",
    hitsPerPage: limit,
  };

  if (filtersStr) {
    searchParams.filters = filtersStr;
  }

  console.log("searchParams", searchParams);

  // Handle sorting - use different index name for sorting
  let targetIndex = indexName;

  try {
    // V5: Use searchSingleIndex method instead of initIndex
    const { hits } = await searchClient.searchSingleIndex({
      indexName: targetIndex,
      searchParams,
    });

    return hits || [];
  } catch (error) {
    console.error("Error querying Algolia:", error);

    // Always try fallback without sorting if there's any error
    console.warn("Trying search without sorting...");
    try {
      const fallbackParams = {
        query: searchTerm || "",
        hitsPerPage: limit,
      };

      if (filtersStr) {
        fallbackParams.filters = filtersStr;
      }

      const { hits } = await searchClient.searchSingleIndex({
        indexName: indexName, // Use original index name
        searchParams: fallbackParams,
      });
      return hits || [];
    } catch (fallbackError) {
      console.error("Fallback query also failed:", fallbackError);
      return [];
    }
  }
}

export const queryFolders = async (
  workspaceData,
  orgData,
  filters,
  limite = null
) => {
  if (!orgData) {
    console.error("Missing orgData");
    return [];
  }

  let combinedFolders = [];

  if (!workspaceData) {
    let foldersQuery = query(
      collection(db, "folder"),
      where("master_id", "==", orgData.id)
    );

    // Add limit to the query
    if (limite) {
      foldersQuery = query(foldersQuery, limit(limite));
    }

    const snapshot = await getDocs(foldersQuery);
    combinedFolders = snapshot.docs.map((doc) => ({
      id: doc.id,
      ref: doc.ref,
      ...doc.data(),
    }));
  } else {
    // Client folders query
    let foldersQueryClient = query(
      collection(db, "folder"),
      where("client_id", "==", orgData.id),
      where("master_id", "==", workspaceData.master_id)
    );

    if (filters && filters.length > 0) {
      filters.forEach((f) => {
        foldersQueryClient = query(
          foldersQueryClient,
          where(f.field, f.operator, f.value)
        );
      });
    }

    // Add limit to client query
    if (limite) {
      foldersQueryClient = query(
        foldersQueryClient,
        limit(Math.ceil(limite / 2))
      );
    }

    // Partners folders query
    let foldersQueryPartners = query(
      collection(db, "folder"),
      where("partners_id", "array-contains", orgData.id),
      where("master_id", "==", workspaceData.master_id)
    );

    if (filters && filters.length > 0) {
      filters.forEach((f) => {
        foldersQueryPartners = query(
          foldersQueryPartners,
          where(f.field, f.operator, f.value)
        );
      });
    }

    // Add limit to partners query
    if (limite) {
      foldersQueryPartners = query(
        foldersQueryPartners,
        limit(Math.ceil(limite / 2))
      );
    }

    // Execute both queries in parallel
    const [clientSnapshot, partnersSnapshot] = await Promise.all([
      getDocs(foldersQueryClient),
      getDocs(foldersQueryPartners),
    ]);

    const clientFolders = clientSnapshot.docs.map((doc) => ({
      id: doc.id,
      ref: doc.ref,
      ...doc.data(),
    }));

    const partnerFolders = partnersSnapshot.docs.map((doc) => ({
      id: doc.id,
      ref: doc.ref,
      ...doc.data(),
    }));

    // Combine and deduplicate folders
    const allFolders = [...clientFolders, ...partnerFolders];
    combinedFolders = Object.values(
      allFolders.reduce((acc, folder) => {
        acc[folder.id] = folder;
        return acc;
      }, {})
    );

    // Apply final limit after deduplication if needed
    if (limite && combinedFolders.length > limite) {
      combinedFolders = combinedFolders.slice(0, limite);
    }
  }

  return combinedFolders.map((folder) => ({
    ...folder,
    ref: folder.ref || doc(db, "folder", folder.id), // Ensure ref is always set
  }));
};

export const queryEvents = async (
  workspaceData,
  orgData,
  filters = [],
  limite = null
) => {
  try {
    if (!orgData) {
      console.error("Missing orgData");
      return [];
    }

    let allEvents = [];

    // Helper function to apply filters to a query
    const applyFilters = (baseQuery, filtersArray) => {
      let filteredQuery = baseQuery;

      if (filtersArray && filtersArray.length > 0) {
        filtersArray.forEach((filter) => {
          const { field, operator, value } = filter;
          filteredQuery = query(filteredQuery, where(field, operator, value));
        });
      }

      return filteredQuery;
    };

    if (!workspaceData) {
      console.log("queryEvents for master org");
      // For master organizations
      let eventsQueryMaster = query(
        collection(db, "event"),
        where("master_id", "==", orgData.id)
      );

      // Apply filters
      eventsQueryMaster = applyFilters(eventsQueryMaster, filters);

      // Apply limit
      if (limite) {
        eventsQueryMaster = query(eventsQueryMaster, limit(limite));
      }
      console.log("eventsQueryMaster", eventsQueryMaster);
      const snapshot = await getDocs(eventsQueryMaster);
      allEvents = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      console.log("allEvents", allEvents);
    } else {
      // For clients and admins: need two separate queries
      let eventsQueryClients = query(
        collection(db, "event"),
        where("client_id", "==", orgData.id),
        where("master_id", "==", workspaceData?.master_id)
      );

      let eventsQueryAdmin = query(
        collection(db, "event"),
        where("admins_id", "array-contains", orgData.id),
        where("master_id", "==", workspaceData?.master_id) // Fixed typo: orkspaceData -> workspaceData
      );

      // Apply filters to both queries
      eventsQueryClients = applyFilters(eventsQueryClients, filters);
      eventsQueryAdmin = applyFilters(eventsQueryAdmin, filters);

      // Apply limit to both queries (split the limit between them)
      if (limite) {
        const splitLimit = Math.ceil(limite / 2);
        eventsQueryClients = query(eventsQueryClients, limit(splitLimit));
        eventsQueryAdmin = query(eventsQueryAdmin, limit(splitLimit));
      }

      // Execute both queries
      const [clientSnapshot, adminSnapshot] = await Promise.all([
        getDocs(eventsQueryClients),
        getDocs(eventsQueryAdmin),
      ]);

      const clientEvents = clientSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const adminEvents = adminSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Combine and deduplicate events
      const combinedEvents = [...clientEvents, ...adminEvents];
      allEvents = Object.values(
        combinedEvents.reduce((acc, event) => {
          acc[event.id] = event;
          return acc;
        }, {})
      );

      // Apply final limit after deduplication if needed
      if (limite && allEvents.length > limite) {
        allEvents = allEvents.slice(0, limite);
      }
    }

    return allEvents.map((event) => ({
      ...event,
      ref: event.ref || doc(db, "event", event.id), // Fixed: changed 'event' to 'ref'
    }));
  } catch (error) {
    console.error("Error querying events:", error);
    return [];
  }
};
